import "./App.css";
import { lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { urlRoutes } from "./constants/page";
import PageLayout from "./layouts/PageLayout";
import useAppInitializer from "./hooks/useAppInitializer";
import { Box, CircularProgress } from "@mui/material";
import AppLayout from "./layouts/AppLayout";
import { SnackbarProvider } from "./contexts/SnackbarContext";
import { ConfirmProvider } from "./contexts/ConfirmContext";
import useIdleSession from "./hooks/useIdleSession";
import ProtectedRoute from "./components/ProtectedRoute";

const Home = lazy(() => import("./routes/home"));
const Login = lazy(() => import("./routes/login"));
const Dashboard = lazy(() => import("./routes/dashboard"));
const TermsOfService = lazy(() => import("./routes/termsOfService"));
const PrivacyPolicy = lazy(() => import("./routes/privacyPolicy"));
const Accounts = lazy(() => import("./routes/accounts"));
const AuditReports = lazy(() => import("./routes/auditReports"));
const CompanyProfile = lazy(() => import("./routes/companyProfile"));
const CompanyUsers = lazy(() => import("./routes/companyUsers"));
const CompanyLocations = lazy(() => import("./routes/companyLocations"));
const CompanyPositions = lazy(() => import("./routes/companyPositions"));
const Forms = lazy(() => import("./routes/forms"));
const Form = lazy(() => import("./routes/form"));
const FormConfiguration = lazy(() => import("./routes/formConfiguration"));
const OtherReports = lazy(() => import("./routes/otherReports"));
const Surveys = lazy(() => import("./routes/surveys"));
const MaintenanceRequests = lazy(() => import("./routes/maintenanceRequests"));
const ITTicketing = lazy(() => import("./routes/itTicketing"));
const IncidentReports = lazy(() => import("./routes/incidentReports"));
const ReportView = lazy(() => import("./routes/reportView"));
const Register = lazy(() => import("./routes/register"));
const SetPassword = lazy(() => import("./routes/setPassword"));
const ForgotPassword = lazy(() => import("./routes/forgotPassword"));
const NotFound404 = lazy(() => import("./routes/notFound404"));
const SignUp = lazy(() => import("./routes/signup"));
const GLAccounts = lazy(() => import("./routes/glAccounts"));
const Assets = lazy(() => import("./routes/assets"));
const PreventativeMaintenance = lazy(() => import("./routes/preventativeMaintenance"));
const AssetParts = lazy(() => import("./routes/assetParts"));
const Vendors = lazy(() => import("./routes/vendors"));
const VendorContacts = lazy(() => import("./routes/vendorContacts"));
const Tasks = lazy(() => import("./routes/tasks"));
const Notifications = lazy(() => import("./routes/notifications"));

function App() {
  const { loading } = useAppInitializer();
  useIdleSession();

  return loading ? (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <Router>
      <SnackbarProvider>
        <ConfirmProvider>
          <AppLayout>
            <Routes>
              <Route exact path={urlRoutes.splash} element={<Home />} />
              <Route
                exact
                path={urlRoutes.dashboard}
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.forms}
                element={
                  <ProtectedRoute>
                    <Forms />
                  </ProtectedRoute>
                }
              />
              <Route exact path={urlRoutes.form} element={<Form />} />
              <Route
                exact
                path={urlRoutes.reportViewDeprecated}
                element={
                  <ProtectedRoute>
                    <ReportView />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.reportView}
                element={
                  <ProtectedRoute>
                    <ReportView />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.surveys}
                element={
                  <ProtectedRoute>
                    <Surveys />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.accounts}
                element={
                  <ProtectedRoute>
                    <Accounts />
                  </ProtectedRoute>
                }
              />
              <Route exact path={urlRoutes.register} element={<Register />} />
              <Route
                exact
                path={urlRoutes.setPassword}
                element={<SetPassword />}
              />
              <Route
                exact
                path={urlRoutes.glAccounts}
                element={
                  <ProtectedRoute>
                    <GLAccounts />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.forgotPassword}
                element={<ForgotPassword />}
              />
              <Route
                exact
                path={urlRoutes.privacy}
                element={<PrivacyPolicy />}
              />
              <Route
                exact
                path={urlRoutes.incidentReports}
                element={
                  <ProtectedRoute>
                    <IncidentReports />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.itTicketing}
                element={
                  <ProtectedRoute>
                    <ITTicketing />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.maintenanceRequests}
                element={
                  <ProtectedRoute>
                    <MaintenanceRequests />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.otherReports}
                element={
                  <ProtectedRoute>
                    <OtherReports />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.formConfiguration}
                element={
                  <ProtectedRoute>
                    <FormConfiguration />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.companyUsers}
                element={
                  <ProtectedRoute>
                    <CompanyUsers />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.companyLocations}
                element={
                  <ProtectedRoute>
                    <CompanyLocations />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.companyPositions}
                element={
                  <ProtectedRoute>
                    <CompanyPositions />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.companyProfile}
                element={
                  <ProtectedRoute>
                    <CompanyProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.auditReports}
                element={
                  <ProtectedRoute>
                    <AuditReports />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.termsOfService}
                element={<TermsOfService />}
              />
              <Route
                exact
                path={urlRoutes.assets}
                element={
                  <ProtectedRoute>
                    <Assets />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.preventativeMaintenance}
                element={
                  <ProtectedRoute>
                    <PreventativeMaintenance />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.disposedAssets}
                element={
                  <ProtectedRoute>
                    <Assets isDisposed />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.assetParts}
                element={
                  <ProtectedRoute>
                    <AssetParts />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.notifications}
                element={
                  <ProtectedRoute>
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.tasks}
                element={
                  <ProtectedRoute>
                    <Tasks />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.vendors}
                element={
                  <ProtectedRoute>
                    <Vendors />
                  </ProtectedRoute>
                }
              />
              <Route
                exact
                path={urlRoutes.vendorContacts}
                element={
                  <ProtectedRoute>
                    <VendorContacts />
                  </ProtectedRoute>
                }
              />
              <Route exact path={urlRoutes.signup} element={<SignUp />} />
              <Route
                exact
                path={urlRoutes.login}
                element={
                  <PageLayout>
                    <Login />
                  </PageLayout>
                }
              />
              <Route path={"*"} element={<NotFound404 />} />
            </Routes>
          </AppLayout>
        </ConfirmProvider>
      </SnackbarProvider>
    </Router>
  );
}

export default App;
